/* Handing the credential back to the CLI's loopback listener.
 *
 * In the body of a POST, never in a URL — `postAuthIntent.ts` already strips a far weaker
 * invite token out of the query string for exactly these reasons (browser history,
 * `Referer`, marketing tags reading the page URL, proxy logs), and this is a refresh token.
 *
 * Two shapes are attempted, in this order, because neither works everywhere:
 *
 *   1. `fetch` with a JSON body. Keeps the user on this page, and the browser enforces the
 *      Origin check for us. But it is a cross-origin request from a public page to a
 *      private address, so Chrome requires a Private Network Access preflight, and
 *      Safari has historically been unwilling to reach http://127.0.0.1 from https at all.
 *   2. A top-level form POST. A *navigation* rather than a subresource, so there is no
 *      CORS, no preflight to be blocked and no mixed-content rule to trip — the token is
 *      still in a request body. The browser leaves this page and lands on the small page
 *      the CLI itself serves.
 *
 * The listener accepts both, and checks the same nonce, Host and Origin either way.
 */

export type CliCredentialPayload = Record<string, string>;

/** How the credential actually got there — reported so the screen can say what happened. */
export type DeliveryOutcome = "delivered" | "navigating";

/** Post to the loopback listener, falling back to a form navigation. */
export async function deliverCliCredential(
  redirectUri: string,
  payload: CliCredentialPayload,
): Promise<DeliveryOutcome> {
  try {
    const response = await fetch(redirectUri, {
      method: "POST",
      // Named explicitly: it is what makes this a preflighted request rather than a
      // "simple" one, and the listener requires a content type it recognises.
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      // No cookies to the loopback — there is no session there and nothing to send.
      credentials: "omit",
      // A local listener answers immediately or not at all; do not follow it anywhere.
      redirect: "error",
    });
    if (response.ok) return "delivered";
  } catch {
    // Blocked by Private Network Access, mixed content, or simply nothing listening.
    // Indistinguishable from here, and the fallback covers all three.
  }

  submitAsForm(redirectUri, payload);
  return "navigating";
}

/** A real form, really submitted: a top-level navigation carrying the token in its body. */
function submitAsForm(
  redirectUri: string,
  payload: CliCredentialPayload,
): void {
  const form = document.createElement("form");
  form.method = "POST";
  form.action = redirectUri;
  // Default enctype is application/x-www-form-urlencoded, which is the second shape the
  // listener accepts. Set explicitly so a future edit does not change it by accident.
  form.enctype = "application/x-www-form-urlencoded";

  for (const [name, value] of Object.entries(payload)) {
    const field = document.createElement("input");
    field.type = "hidden";
    field.name = name;
    field.value = value;
    form.appendChild(field);
  }

  // Must be in the document to submit.
  document.body.appendChild(form);
  form.submit();
}
